import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  makeStyles,
  TextField,
  Typography,
} from "@material-ui/core";
import { ToggleButton, ToggleButtonGroup } from "@material-ui/lab";
import React from "react";
import { Controller, useForm } from "react-hook-form";
import { toast } from "react-toastify";
import apiHelper from "../../../../util/ApiHelper";
import { useLoading } from "../../../../util/LoadingProvider";
import FormHelperText from '@material-ui/core/FormHelperText';
import dayjs from "dayjs";
import { modal } from "../../../../util";
import InputLabel from '@material-ui/core/InputLabel';
import FormControl from '@material-ui/core/FormControl';
import Select from '@material-ui/core/Select';
import styled from "styled-components";
import ClearOutlinedIcon from "@material-ui/icons/ClearOutlined";

const useStyles = makeStyles((theme) => ({
  innerBox: {
    background: "#ffffff",
    marginBottom: theme.spacing(4),
    "& *": {
      fontSize: "13px",
      lineHeight: "15px",
    },
  },
  sectionBox: {
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(3),
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(3),
  },
  sectionTitle: {
    fontWeight: "300",
    fontSize: "13px",
    lineHeight: "15px",
    color: "#333333",
  },
  itemTitle: {
    fontWeight: "bold",
    fontSize: "12px",
    lineHeight: "14px",
    color: "#333333",
  },
  itemValue: {
    fontWeight: "normal",
    fontSize: "14px",
    lineHeight: "16px",
    color: "#333333",
  },
  input: {
    width: "100%",
    "& button": {
      fontSize: "13px",
      lineHeight: "15px",
      width: "25%",
    },
  },
  buttonGroup: {
    textAlign: "right",
    marginTop: "-20px",
    "& button": {
      color: "#306F9D",
      fontSize: "13px",
      textTransform: "inherit",
    },
  },
  customLabel: {
    margin: "0 !important",
    "& label": {
      color: "black",
      fontWeight: 700,
      fontSize: "16px",
    },
    "& p": {
      fontSize: "12px",
    },
  },
}));

const A = styled.a`
  color: #306F9D;
  text-decoration: none;
`;

const defaultFormValues = {
  "hkiffId": process.env.REACT_APP_HKIFF_ID_NO,
  "regionId": null,
  "districtId": null,
  "type": "screening",
  "localizations": {
    "en": {
      "name": "",
      "address": "",
      "remark": ""
    },
    "tc": {
      "name": "",
      "address": "",
      "remark": ""
    }
  },
  "longitude": "",
  "latitude": "",
  "tel": "",
  "status": {
    "status": true,
    "deleted": false
  },
  "isAppVisible": true,
  "image": null,
  "file": null, // frontend only
  updateTime: dayjs().format("YYYY-MM-DDTHH:mm:ss[Z]"),
};

function VenueEditPage({ disabled, ...props }) {
  const classes = useStyles();
  const { match, history } = props;
  const { id } = match.params;
  const [paramId, setParamId] = React.useState(id);
  const [defaultValue, setDefaultValue] = React.useState(defaultFormValues);
  const { setLoading } = useLoading();
  const [districtList, setDistrictList] = React.useState([]);

  const {
    control,
    handleSubmit,
    getValues,
    setValue,
    reset,
    setError,
    watch
  } = useForm({
    defaultValues: React.useMemo(() => defaultValue, [defaultValue]),
  });

  const watchFile = watch(["image", "file"]);

  const uploadFile = async (file) => {
    const result = await apiHelper.hkiff.uploadHKIFFFile(file, "VENUECODE");
    return result;
  }

  const onSubmit = async (value) => {
    setLoading(true);
    try {
      // purge date and time
      const { date, time, ...data } = value;
      // districtId is validated by form (cannot be None/empty)

      const districtDetail = await apiHelper.hkiff.getDistrictById(data.districtId);
      if (districtDetail.status === 200) {
        data.regionId = districtDetail.data.regionId;
      }

      if (data.file) {
        const result = await uploadFile(data.file);
        if (result.status === 200) {
          data.image = result.data.url;
        } else {
          throw new Error("Upload Image failed");
        }
      }

      if (data.regionId != null) {
        //delete frontend only data
        delete data.file;
        
        if (paramId !== "new") {
          const result = await apiHelper.hkiff.updateVenueById(paramId, data);
          if (result.status === 200) {
            toast.dark("Venue is updated.");
          }
        } else {
          const result = await apiHelper.hkiff.createVenue(data);
          if (result.status === 200) {
            setParamId(result.data.id);
            history.push(`/hkiff/region-management/venue/${result.data.id}`);
            toast.dark("Venue is created.");
          }
        }
      }
    } catch (e) {
      console.log(e);
    } finally {
      setLoading(false);
    }
  };

  const getVenueById = React.useCallback(async () => {
    try {
      setLoading(true);
      const result = await apiHelper.hkiff.getVenueById(id);
      const data = result.data;
      setDefaultValue(data);

      reset(data);
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  }, [id, reset, setLoading]);

  const getDistrictList = React.useCallback(async () => {
    const districts = await apiHelper.hkiff.getAllDistricts();
    setDistrictList(districts.data);
  }, [])

  React.useEffect(() => {
    getDistrictList();
    if (id !== "new") getVenueById();
  }, [getVenueById, id]);

  const deleteVenue = React.useCallback(async (id) => {
    const decision = await modal.fire({
      title: "Delete Venue",
      text: "Are you sure to delete this venue?",
      showCancelButton: true,
      confirmButtonText: `Delete`,
    });
    if (decision.isConfirmed) {
      try {
        const result = await apiHelper.hkiff.deleteVenueById(id);
        if (result.data.code === 0) {
          history.push(`/hkiff/region-management/venue`);
          toast.dark("Venue deleted.");
        }
      } catch (err) {
        console.log(err);
      }
    }
  }, []);

  function DistrictOptions(props) {
    const { district } = props;
    return (districtList.map((value, index) => {
      return (<option value={value.id}>{value.name}</option>)
    }))
  }

  const handleDeleteImage = async () => {
    const decision = await modal.fire({
      title: "Delete venue image",
      text: "Are you sure to delete the current venue image?",
      showCancelButton: true,
      confirmButtonText: `Delete`,
    });
    if (decision.isConfirmed) {
      setValue("image", null);
    }
  }

  const handleImageFileChange = (e) => {
    if (e.target.files[0]) {
			setValue("file", e.target.files[0]);
		};
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        {`Venue Management > ${id === "new" ? "Add" : "Edit"} Venue`}
      </Typography>
      <form onSubmit={handleSubmit(onSubmit)}>
        <fieldset disabled={disabled} className="fieldset">


          <Grid container xs={8} direction="row" spacing={3}>
            <Grid item xs={12}>
              <Controller
                name="localizations.en.name"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    label="Venue Title (English)"
                    variant="outlined"
                    margin="dense"
                    className={classes.textInput}
                    fullWidth
                    {...field}
                    error={!!error}
                    helperText={error ? error.message : ""}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="localizations.tc.name"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    label="Venue Name (Traditional Chinese)"
                    variant="outlined"
                    margin="dense"
                    className={classes.textInput}
                    fullWidth
                    {...field}
                    error={!!error}
                    helperText={error ? error.message : ""}
                  />
                )}
              />
            </Grid>

            <Grid container item xs={12} spacing={3}>
              <Grid item xs={4}>
                <Controller
                  control={control}
                  name="type"
                  render={({
                    field: { value, onChange, ...rest },
                    fieldState: { error },
                  }) => (
                    <ToggleButtonGroup
                      {...rest}
                      value={value}
                      onChange={(event, newFormats) => {
                        onChange(newFormats);
                      }}
                      exclusive
                      aria-label="Type"
                      className={classes.input}
                    >
                      <ToggleButton
                        value="screening"
                        aria-label="Screening Venue"
                        style={{ width: "33.3%" }}
                      >
                        Screening Venue
                          </ToggleButton>
                      <ToggleButton
                        value="tdminfo"
                        aria-label="TdmInfo Venue"
                        style={{ width: "33.3%" }}
                      >
                        TdmInfo Venue
                          </ToggleButton>

                      <ToggleButton
                        value="urbtix"
                        aria-label="Urbtix Venue"
                        style={{ width: "33.3%" }}
                      >
                        Urbtix Venue
                          </ToggleButton>
                    </ToggleButtonGroup>
                  )}
                />
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <Controller
                name="districtId"
                control={control}
                rules={{
                  required: "Please select a district",
                  validate: (v) =>
                    (v != null && v !== "") || "Please select a district",
                }}
                render={({
                  field: { value, onChange, ...fieldRest },
                  fieldState: { error },
                }) => (
                  <FormControl
                    variant="outlined"
                    margin="dense"
                    className={classes.formControl}
                    style={{ width: "100%" }}
                    error={!!error}
                  >
                    <InputLabel
                      htmlFor="outlined-district-native-simple"
                      shrink
                    >
                      District
                    </InputLabel>
                    <Select
                      native
                      {...fieldRest}
                      value={value ?? ""}
                      onChange={onChange}
                      label="District"
                      inputProps={{
                        name: "districtId",
                        id: "outlined-district-native-simple",
                      }}
                    >
                      <option value="">None</option>
                      <DistrictOptions districtList={districtList} />
                    </Select>
                    {error && (
                      <FormHelperText error>{error.message}</FormHelperText>
                    )}
                  </FormControl>
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Controller
                name="localizations.en.address"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    label="Address (English)"
                    variant="outlined"
                    margin="dense"
                    className={classes.textInput}
                    fullWidth
                    {...field}
                    error={!!error}
                    helperText={error ? error.message : ""}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Controller
                name="localizations.tc.address"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    label="Address (Traditional Chinese)"
                    variant="outlined"
                    margin="dense"
                    className={classes.textInput}
                    fullWidth
                    {...field}
                    error={!!error}
                    helperText={error ? error.message : ""}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Controller
                name="longitude"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    label="Longitude"
                    variant="outlined"
                    margin="dense"
                    className={classes.textInput}
                    fullWidth
                    {...field}
                    error={!!error}
                    helperText={error ? error.message : ""}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Controller
                name="latitude"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    label="Latitude"
                    variant="outlined"
                    margin="dense"
                    className={classes.textInput}
                    fullWidth
                    {...field}
                    error={!!error}
                    helperText={error ? error.message : ""}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Controller
                name="tel"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    label="Tel"
                    variant="outlined"
                    margin="dense"
                    className={classes.textInput}
                    fullWidth
                    {...field}
                    error={!!error}
                    helperText={error ? error.message : ""}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Controller
                name="localizations.en.remark"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    label="Remark (English)"
                    variant="outlined"
                    margin="dense"
                    className={classes.textInput}
                    fullWidth
                    {...field}
                    error={!!error}
                    helperText={error ? error.message : ""}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Controller
                name="localizations.tc.remark"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    label="Remark (Traditional Chinese)"
                    variant="outlined"
                    margin="dense"
                    className={classes.textInput}
                    fullWidth
                    {...field}
                    error={!!error}
                    helperText={error ? error.message : ""}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                htmlFor="raised-button-file"
                label={
                  watchFile[0]
                  ? "Change Venue Image"
                  : "Venue Image"
                }
                value={watchFile[1]?.name ?? ""}
                variant="outlined"
                placeholder={watchFile[0] ? "Change Venue Image" : "Venue Image"}
                fullWidth
                InputProps={{
                  classes: {
                    input: classes["&::placeholder"],
                  },
                  endAdornment: (
                    <Controller
                      control={control}
                      name="file"
                      render={() => {
                        return (
                          <InputAdornment position="end">
                            <input
                              style={{ display: "none" }}
                              id="raised-button-file"
                              type="file"
                              onChange={handleImageFileChange}
                            />
                            <Button
                              htmlFor="raised-button-file"
                              variant="outlined"
                              component="label"
                            >
                              Choose
                            </Button>
                          </InputAdornment>
                        );
                      }}
                    />
                  ),
                  startAdornment: (
                    <InputAdornment
                      position="start"
                      style={watchFile[1] ? {} : { display: "none" }}
                    >
                      <IconButton
                        component="label"
                        size="small"
                        onClick={() => {
                          setValue("file", null);
                        }}
                      >
                        <ClearOutlinedIcon fontSize="inherit" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                style={watchFile[0] ? { marginBottom: "0" } : {}}
              />
            </Grid>
            {getValues('image') && (
              <Grid item xs={12}>
                <img
                    src={getValues('image')}
                    style={{
                        maxHeight: "30px",
                        maxWidth: "30px",
                        cursor: "pointer"
                    }}
                    onClick={() => window.open(getValues('image'), '_blank').focus()}
                    alt=""
                />
                &nbsp;
                <A href={getValues('image')}
                  target="_blank">
                    <small>{getValues('image').split("/").pop()}</small>
                </A>
                {disabled || (
                    <small
                        style={{
                            marginLeft: "20px",
                            textDecoration: "underline",
                            color: "#962013",
                            cursor: "pointer",
                        }}
                        onClick={handleDeleteImage}
                    >
                        Delete
                    </small>
                )}
              </Grid>
            )}

            <Grid item xs={12}>
              <Controller
                name="isAppVisible"
                control={control}
                render={({ field: { value, onChange }}) => {
                  return (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={value === true}
                          type="checkbox"
                          onChange={onChange}
                          color="primary"
                        />
                      }
                      label="Display venue on app" 
                      />
                  )}}
                />  
            </Grid>

            <Grid container item xs={12} spacing={3}>
              <Grid item xs={4}>
                <Controller
                  control={control}
                  name="status.status"
                  render={({
                    field: { value, onChange, ...rest },
                    fieldState: { error },
                  }) => (
                    <ToggleButtonGroup
                      {...rest}
                      value={value}
                      onChange={(event, newFormats) => {
                        onChange(newFormats);
                      }}
                      exclusive
                      aria-label="Status"
                      className={classes.input}
                    >
                      <ToggleButton
                        value={true}
                        aria-label="Active"
                        style={{ width: "50%" }}
                      >
                        Active
                          </ToggleButton>
                      <ToggleButton
                        value={false}
                        aria-label="Inactive"
                        style={{ width: "50%" }}
                      >
                        Inactive
                          </ToggleButton>
                    </ToggleButtonGroup>
                  )}
                />
              </Grid>
            </Grid>

          </Grid>


          <div className={classes.buttonGroup}>
            {disabled || (
              <div className={classes.buttonGroup}>
                <Button
                  type="submit"
                  variant="contained"
                  disableElevation
                  style={{
                    color: "white",
                    background: "#306F9D",
                    marginRight: "5px",
                    padding: "9px 16px",
                  }}
                >
                  Save Changes
                </Button>
                {match.params.id != "new" && (
                  <Button
                    variant="contained"
                    disableElevation
                    style={{
                      color: "white",
                      background: "#962013",
                      padding: "9px 16px",
                    }}
                    onClick={() => {
                      deleteVenue(match.params.id);
                    }}
                  >
                    Delete Venue
                  </Button>
                )}
              </div>
            )}
          </div>
        </fieldset>
      </form>
    </Box>
  );
}

export default VenueEditPage;
